/**
 * API keys: created here with the signed-in user's own permissions (the most a key may have), shown once, and
 * revoked. Before, a key could be made only with `POST /v1/api-keys`.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Copy } from "lucide-react";
import { useId, useState } from "react";
import { data, type Schemas } from "../api/client";
import { useBff, useCan, useMe } from "../app/session";
import { useFormat, useT } from "../i18n";
import { Button } from "../ui/Button";
import { Empty, ErrorState } from "../ui/states";
import { Table } from "../ui/Table";

type Key = Schemas["ApiKeyOut"];
type Scope = Schemas["ApiKeyCreate"]["scopes"][number];

export function ApiKeys({ keys }: { keys: Key[] }) {
  const t = useT();
  const id = useId();
  const format = useFormat();
  const { api } = useBff();
  const me = useMe();
  const canManage = useCan("keys:manage");
  const queries = useQueryClient();
  const [name, setName] = useState("");
  const [made, setMade] = useState<Schemas["ApiKeyCreated"] | null>(null);
  const [copied, setCopied] = useState(false);
  const refresh = () => queries.invalidateQueries({ predicate: (q) => String(q.queryKey[0]) === "api-keys" });

  const create = useMutation({
    mutationFn: () => data(api.POST("/v1/api-keys", { body: { name: name.trim(), scopes: me.permissions as Scope[], mode: "live" } })),
    onSuccess: async (key) => {
      setMade(key);
      setCopied(false);
      setName("");
      await refresh();
    },
  });
  const revoke = useMutation({
    mutationFn: (keyId: string) => data(api.DELETE("/v1/api-keys/{key_id}", { params: { path: { key_id: keyId } } })),
    onSuccess: refresh,
  });
  const live = keys.filter((k) => !k.revoked_at);

  return (
    <div className="space-y-4">
      {live.length === 0 ? (
        <Empty title={t("developers.keysEmpty")}>
          <p>{t("developers.keysEmptyBody")}</p>
        </Empty>
      ) : (
        <Table
          caption={t("developers.keys")}
          rows={live}
          rowKey={(k) => k.id}
          columns={[
            { header: t("developers.col.name"), cell: (k) => <span className="font-medium">{k.name}</span> },
            { header: t("developers.col.prefix"), cell: (k) => <span className="font-mono">{k.prefix}…</span> },
            { header: t("developers.col.lastUsed"), cell: (k) => (k.last_used_at ? format.dateTime(k.last_used_at) : t("developers.never")) },
            ...(canManage ? [{
              header: t("developers.keys.actions"),
              cell: (k: Key) => (
                <Button variant="secondary" disabled={revoke.isPending}
                  onClick={() => { if (window.confirm(t("developers.keys.revokeConfirm", { name: k.name }))) revoke.mutate(k.id); }}>
                  {t("developers.keys.revoke")}
                </Button>
              ),
            }] : []),
          ]}
        />
      )}
      {revoke.isError && <ErrorState error={revoke.error} />}
      {made && (
        <div role="status" className="space-y-2 rounded-md border border-[var(--color-warning)] p-3 text-sm">
          <p className="font-medium">{t("developers.keys.madeTitle", { name: made.name })}</p>
          <p>{t("developers.keys.madeBody")}</p>
          <div className="flex flex-wrap items-center gap-2">
            <code aria-label={t("developers.keys.theKey")} className="break-all rounded bg-[var(--color-surface)] px-2 py-1 font-mono text-xs">{made.key}</code>
            <Button variant="secondary" onClick={async () => { await navigator.clipboard.writeText(made.key); setCopied(true); }}>
              <Copy size={14} aria-hidden="true" />
              {copied ? t("developers.keys.copied") : t("developers.keys.copy")}
            </Button>
            <Button variant="secondary" onClick={() => setMade(null)}>{t("developers.keys.done")}</Button>
          </div>
        </div>
      )}
      {canManage && (
        <form className="flex flex-wrap items-end gap-2" onSubmit={(e) => { e.preventDefault(); if (name.trim()) create.mutate(); }}>
          <label htmlFor={`${id}-name`} className="flex flex-col gap-1 text-sm">
            {t("developers.keys.name")}
            <input id={`${id}-name`} value={name} onChange={(e) => setName(e.target.value)} maxLength={100}
              className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1.5" />
          </label>
          <Button type="submit" disabled={!name.trim() || create.isPending}>{t("developers.keys.create")}</Button>
          <p className="w-full text-xs text-[var(--color-muted)]">{t("developers.keys.createHint")}</p>
        </form>
      )}
      {create.isError && <ErrorState error={create.error} />}
    </div>
  );
}
